import React, { useEffect, useState } from "react";
import axios from "axios";
import Input from "../../CoreComponent/Input";
import ImageUpload from "../../CoreComponent/ImageUpload";
import "./style.scss";
import TextArea from "../../CoreComponent/TextArea";
import SVG from "react-inlinesvg";
import deleteIcon from "../../icons/deleteIcon.svg";
import toast from "react-hot-toast";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Drawer,
  IconButton,
  Typography,
  Button,
  Box,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";

/* Util: local YYYY-MM-DD (avoids UTC issues) */
const getTodayLocal = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/* DateInput component */
export const DateInput = ({
  label,
  placeholder,
  inputValue,
  setInputValue,
  min,
  required,
  className,
}) => {
  return (
    <div className={`date-input ${className || ""}`}>
      {label && <label className="date-label">{label}</label>}
      <input
        type="date"
        value={inputValue || ""}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={placeholder}
        min={min}
        required={required}
      />
    </div>
  );
};

/* Committee form */
const CommitteeForm = ({
  committeeMembers,
  setCommitteeMembers,
  countriesList,
}) => {
  const addCommitteeMember = () => {
    setCommitteeMembers([
      ...committeeMembers,
      {
        id: Date.now(),
        name: "",
        title: "",
        committee_type: "",
        country: "",
        conference_id: "",
        image: null,
      },
    ]);
  };

  const deleteCommitteeMember = (id) => {
    setCommitteeMembers(committeeMembers.filter((member) => member.id !== id));
  };

  const handleMemberChange = (id, key, value) => {
    const updatedMembers = committeeMembers.map((member) =>
      member.id === id ? { ...member, [key]: value } : member
    );
    setCommitteeMembers(updatedMembers);
  };

  const getByName = (name) =>
    (countriesList || []).find((c) => c.name === name) || null;

  return (
    <div className="committee-form-container">
      <div className="title-committee">Committee Members</div>
      <div className="button-section-container">
        <Button className="add-button-committee" onClick={addCommitteeMember}>
          Add Member
        </Button>
      </div>
      {committeeMembers.map((member) => (
        <div key={member.id} className="committee-member">
          <div className="member-info">
            <Input
              type="text"
              label="Name"
              placeholder="Enter name"
              inputValue={member.name}
              setInputValue={(value) =>
                handleMemberChange(member.id, "name", value)
              }
              className="name-input"
            />
            <Input
              type="text"
              label="Title"
              placeholder="Enter title"
              inputValue={member.title}
              setInputValue={(value) =>
                handleMemberChange(member.id, "title", value)
              }
              className="title-input"
            />
            <Input
              type="text"
              label="Committee Type"
              placeholder="Enter committee type"
              inputValue={member.committee_type}
              setInputValue={(value) =>
                handleMemberChange(member.id, "committee_type", value)
              }
              className="committee-type-input"
            />
            <Box>
              <FormControl fullWidth sx={{ marginTop: "8px", height: "auto" }}>
                <InputLabel id={`country-label-${member.id}`}>
                  Country
                </InputLabel>
                <Select
                  sx={{ height: "40px" }}
                  labelId={`country-label-${member.id}`}
                  value={member.country || ""}
                  onChange={(e) =>
                    handleMemberChange(member.id, "country", e.target.value)
                  }
                  label="Country"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected)
                      return (
                        <span style={{ color: "#888" }}>Select country</span>
                      );
                    const item = getByName(selected);
                    return (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {item?.flag_url ? (
                          <img
                            src={item.flag_url}
                            alt={item.name}
                            width={20}
                            height={14}
                            style={{
                              objectFit: "cover",
                              borderRadius: 2,
                            }}
                          />
                        ) : null}
                        <span>{selected}</span>
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        overflowY: "auto",
                        zIndex: 1000,
                      },
                    },
                  }}
                >
                  {(countriesList || []).map((c) => (
                    <MenuItem key={c.id} value={c.name}>
                      <ListItemIcon>
                        <img
                          src={c.flag_url}
                          alt={c.name}
                          width={20}
                          height={14}
                          style={{
                            objectFit: "cover",
                            borderRadius: 2,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary={c.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <ImageUpload
              label="Upload Image"
              inputValue={member.image}
              setInputValue={(file) =>
                handleMemberChange(member.id, "image", file)
              }
              allowedExtensions={["jpg", "jpeg", "png"]}
              required={false}
            />
          </div>
          <Button
            className="delete-button-committee"
            onClick={() => deleteCommitteeMember(member.id)}
          >
            Delete
          </Button>
        </div>
      ))}
    </div>
  );
};

/* ✅ FIXED PriceForm - Error Resolved */
const PriceForm = ({ entries, setEntries }) => {
  const addEntry = () => {
    setEntries([
      ...entries,
      { 
        id: Date.now(), 
        price_type: "", 
        price: "", 
        description: "",
        whats_include: [{ id: Date.now() + 1, text: "" }]
      },
    ]);
  };

  const deleteEntry = (id) => {
    setEntries(entries.filter((entry) => entry.id !== id));
  };

  const handleInputChange = (id, key, value) => {
    const updatedEntries = entries.map((entry) =>
      entry.id === id ? { ...entry, [key]: value } : entry
    );
    setEntries(updatedEntries);
  };

  // What's Included handlers
  const addWhatsInclude = (entryId) => {
    const updatedEntries = entries.map((entry) =>
      entry.id === entryId
        ? {
            ...entry,
            whats_include: [
              ...(entry.whats_include || []),
              { id: Date.now(), text: "" },
            ],
          }
        : entry
    );
    setEntries(updatedEntries);
  };

  const deleteWhatsInclude = (entryId, includeId) => {
    const updatedEntries = entries.map((entry) =>
      entry.id === entryId
        ? {
            ...entry,
            whats_include: (entry.whats_include || []).filter(
              (item) => item.id !== includeId
            ),
          }
        : entry
    );
    setEntries(updatedEntries);
  };

  const handleWhatsIncludeChange = (entryId, includeId, value) => {
    const updatedEntries = entries.map((entry) =>
      entry.id === entryId
        ? {
            ...entry,
            whats_include: (entry.whats_include || []).map((item) =>
              item.id === includeId ? { ...item, text: value } : item
            ),
          }
        : entry
    );
    setEntries(updatedEntries);
  };

  return (
    <div className="price-form-container">
      <div className="price-header">Add Pricing Information</div>
      <div className="button-section-container">
        <Button className="add-button-pricing" onClick={addEntry}>
          Add Entry
        </Button>
      </div>
      {entries.map((entry) => (
        <div key={entry.id} className="entry-row">
          <Input
            label="Price Type"
            placeholder="Enter Price Type"
            inputValue={entry.price_type}
            setInputValue={(value) =>
              handleInputChange(entry.id, "price_type", value)
            }
            type="text"
          />
          
          {/* ✅ FIXED: setInputValue instead of setInputChange */}
          <Input
            label="Price (USD)"
            placeholder="Enter Price"
            inputValue={entry.price}
            setInputValue={(value) =>  // ← YE LINE FIX KI
              handleInputChange(entry.id, "price", value)
            }
            type="number"
          />

          {/* What's Included Section */}
          <div className="whats-included-section" style={{ margin: "20px 0" }}>
            <div className="whats-included-title" style={{ 
              fontWeight: "bold", 
              marginBottom: "12px",
              fontSize: "16px"
            }}>
              What's included in this conference
            </div>
            <div className="whats-include-items">
              {(entry.whats_include || []).map((includeItem) => (
                <div key={includeItem.id} className="whats-include-row" style={{ 
                  display: "flex", 
                  gap: "10px", 
                  alignItems: "end",
                  marginBottom: "8px"
                }}>
                  <Input
                    placeholder="Enter what's included"
                    inputValue={includeItem.text}
                    setInputValue={(value) =>
                      handleWhatsIncludeChange(entry.id, includeItem.id, value)
                    }
                    type="text"
                    style={{ flex: 1 }}
                  />
                  <Button
                    className="delete-whats-include"
                    onClick={() => deleteWhatsInclude(entry.id, includeItem.id)}
                    size="small"
                    variant="outlined"
                    style={{ minWidth: "80px" }}
                  >
                    Delete
                  </Button>
                </div>
              ))}
              <Button
                className="add-whats-include"
                onClick={() => addWhatsInclude(entry.id)}
                size="small"
                variant="outlined"
                style={{ marginTop: "8px" }}
              >
                + Add More
              </Button>
            </div>
          </div>

          <Input
            label="Description"
            placeholder="Enter Description"
            inputValue={entry.description}
            setInputValue={(value) =>
              handleInputChange(entry.id, "description", value)
            }
            type="text"
          />
          
          <Button
            className="delete-button-entry"
            onClick={() => deleteEntry(entry.id)}
          >
            Delete Entry
          </Button>
        </div>
      ))}
    </div>
  );
};

const ConferencesAdmin = ({ setIsOpen, getConference }) => {
  const BaseUrl = process.env.REACT_APP_BASE_URL;

  const [committeeMembers, setCommitteeMembers] = useState([
    {
      id: Date.now(),
      name: "",
      title: "",
      committee_type: "",
      country: "",
      conference_id: "",
      image: null,
    },
  ]);
  
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [location, setLocation] = useState("");
  const [image, setImage] = useState(null);
  const [firstAnnouncement, setFirstAnnouncement] = useState(null);
  const [secondAnnouncement, setSecondAnnouncement] = useState(null);
  const [brochure, setBrochure] = useState(null);
  const [scientificProgram, setScientificProgram] = useState(null);
  const [visaCost, setVisaCost] = useState("");
  const [companionDinnerPrice, setCompanionDinnerPrice] = useState("");
  const [logo, setLogo] = useState("");
  const [slogo, setSLogo] = useState(null);
  
  const [entries, setEntries] = useState([
    { 
      id: Date.now(), 
      price_type: "", 
      price: "", 
      description: "",
      whats_include: [{ id: Date.now() + 1, text: "" }]
    }
  ]);
  
  const [topics, setTopics] = useState([""]);
  const [countriesList, setCountriesList] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const token =
      sessionStorage.getItem("adminToken") ||
      sessionStorage.getItem("token") ||
      localStorage.getItem("adminToken") ||
      localStorage.getItem("token");
    axios
      .get("https://eventscons.com/eventbackendnew/api/countries", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res?.data?.data || [];
        if (isMounted) setCountriesList(data);
      })
      .catch((err) => {
        console.error("Countries fetch failed", err);
        setCountriesList([]);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const today = getTodayLocal();

  function convertPriceToObject(data) {
    let result = {};
    data.forEach((item, index) => {
      result[`prices[${index}][price_description]`] = item.description || "";
      result[`prices[${index}][price]`] = item.price || "";
      result[`prices[${index}][price_type]`] = item.price_type || "";
      
      if (item.whats_include && item.whats_include.length > 0) {
        item.whats_include.forEach((includeItem, includeIndex) => {
          result[`prices[${index}][whats_include][${includeIndex}]`] = includeItem.text || "";
        });
      }
    });
    return result;
  }

  const handleTopicChange = (index, newValue) => {
    const updatedTopics = [...topics];
    updatedTopics[index] = newValue;
    setTopics(updatedTopics);
  };

  const handleAddTopic = () => setTopics([...topics, ""]);
  const handleRemoveTopic = (index) =>
    setTopics(topics.filter((_, i) => i !== index));

  async function addCommitteeMembers(mainId, members) {
    const formData = new FormData();
    const token =
      sessionStorage.getItem("adminToken") ||
      sessionStorage.getItem("token") ||
      localStorage.getItem("adminToken") ||
      localStorage.getItem("token");

    members.forEach((member, index) => {
      formData.append(`members[${index}][name]`, member.name || "");
      formData.append(`members[${index}][title]`, member.title || "");
      formData.append(
        `members[${index}][committee_type]`,
        member.committee_type || ""
      );
      formData.append(`members[${index}][country]`, member.country || "");
      formData.append(`members[${index}][conference_id]`, mainId);
      if (member.image) {
        formData.append(
          `members[${index}][committee_image]`,
          member.image
        );
      }
    });

    try {
      await axios.post(`${BaseUrl}/con/committee`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsOpen(false);
      getConference();
    } catch (error) {
      console.error("Error:", error);
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();

    const token =
      sessionStorage.getItem("adminToken") ||
      sessionStorage.getItem("token") ||
      localStorage.getItem("adminToken") ||
      localStorage.getItem("token");

    if (!startDate || !endDate || startDate < today || endDate < startDate) {
      toast.error(
        "Please select valid future dates: End Date must be on/after Start Date."
      );
      return;
    }

    const prices = convertPriceToObject(entries);
    const formData = new FormData();

    for (const key in prices) {
      if (Object.prototype.hasOwnProperty.call(prices, key)) {
        formData.append(key, prices[key]);
      }
    }

    formData.append("title", title);
    formData.append("description", description);
    formData.append("start_date", startDate);
    formData.append("end_date", endDate);
    formData.append("location", location);
    formData.append("image", image);
    formData.append("logo", logo);
    formData.append("second_logo", slogo);
    formData.append("first_announcement_pdf", firstAnnouncement);
    formData.append("second_announcement_pdf", secondAnnouncement);
    formData.append("conference_brochure_pdf", brochure);
    formData.append("conference_scientific_program_pdf", scientificProgram);
    formData.append("visa_price", visaCost || 0);
    formData.append("companion_dinner_price", companionDinnerPrice || 0);
    formData.append("scientific_topics", topics.join(","));
    formData.append("timestamps", new Date().toISOString());

    axios
      .post(`${BaseUrl}/con`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const id = response.data.id;
        toast.success("Conference created successfully!");
        addCommitteeMembers(id, committeeMembers);
        setIsOpen(false);
        getConference();
      })
      .catch((error) => {
        console.error("Error submitting data: ", error);
        toast.error("Error creating conference!");
      });
  };

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={() => setIsOpen(false)}
      sx={{
        zIndex: (theme) => theme.zIndex.modal,
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "100%", md: "50%" },
          padding: "24px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        },
      }}
    >
      <div style={{ display: "flex", justifyContent: "flex-end", padding: 2 }}>
        <IconButton onClick={() => setIsOpen(false)}>
          <CloseRounded />
        </IconButton>
      </div>
      <Typography
        variant="h6"
        sx={{
          color: "#c62828",
          textAlign: "center",
          backgroundColor: "#f1f1f1",
          padding: 1,
          borderRadius: 1,
          marginBottom: 2,
        }}
      >
        Add New Conference
      </Typography>
      <div className="form-section" style={{ overflowY: "auto" }}>
        <Input
          label="Title"
          placeholder="Enter title"
          inputValue={title}
          setInputValue={setTitle}
          type="text"
          required
        />
        <TextArea
          label="Description"
          placeholder="Enter description"
          value={description}
          setValue={setDescription}
          type="text"
          required
        />

        <div className="date-form-custom">
          <DateInput
            label="Start Date"
            className="dateinput"
            placeholder="YYYY-MM-DD"
            inputValue={startDate}
            setInputValue={(val) => {
              setStartDate(val);
              if (endDate && endDate < val) setEndDate(val);
            }}
            min={today}
            required
          />
          <DateInput
            className="dateinput"
            label="End Date"
            placeholder="YYYY-MM-DD"
            inputValue={endDate}
            setInputValue={setEndDate}
            min={startDate || today}
            required
          />
        </div>

        <Input
          label="Location"
          placeholder="Enter location"
          inputValue={location}
          setInputValue={setLocation}
          type="text"
          required
        />

        <ImageUpload
          label="Upload Logo"
          inputValue={logo}
          setInputValue={setLogo}
          allowedExtensions={["jpg", "jpeg", "png"]}
        />
        <ImageUpload
          label="Upload Second Logo"
          inputValue={slogo}
          setInputValue={setSLogo}
          allowedExtensions={["jpg", "jpeg", "png"]}
        />
        <ImageUpload
          label="Upload Image"
          inputValue={image}
          setInputValue={setImage}
          allowedExtensions={["jpg", "jpeg", "png"]}
        />

        <ImageUpload
          label="First Announcement PDF"
          inputValue={firstAnnouncement}
          setInputValue={setFirstAnnouncement}
          allowedExtensions={["pdf"]}
        />
        <ImageUpload
          label="Second Announcement PDF"
          inputValue={secondAnnouncement}
          setInputValue={setSecondAnnouncement}
          allowedExtensions={["pdf"]}
        />
        <ImageUpload
          label="Conference Brochure PDF"
          inputValue={brochure}
          setInputValue={setBrochure}
          allowedExtensions={["pdf"]}
        />
        <ImageUpload
          label="Conference Scientific Program PDF"
          inputValue={scientificProgram}
          setInputValue={setScientificProgram}
          allowedExtensions={["pdf"]}
        />

        <Input
          label="Visa Cost (USD)"
          placeholder="Enter visa cost"
          inputValue={visaCost}
          setInputValue={setVisaCost}
          type="number"
          required
        />
        <Input
          label="Companion Dinner Cost (USD)"
          placeholder="Enter companion Dinner Price Cost"
          inputValue={companionDinnerPrice}
          setInputValue={setCompanionDinnerPrice}
          type="number"
          required
        />

        <div className="topics-container">
          <div className="topic-title">
            Topics <span className="star">*</span>
          </div>
          <div className="topics-container-inputs">
            {topics.map((topic, index) => (
              <div key={index} className="topic-input-container">
                <Input
                  placeholder="Enter a topic"
                  inputValue={topic}
                  setInputValue={(newValue) =>
                    handleTopicChange(index, newValue)
                  }
                />
                <SVG
                  className="delete-icon"
                  src={deleteIcon}
                  onClick={() => handleRemoveTopic(index)}
                />
              </div>
            ))}
            <div className="add-topic-btn-container">
              <Button
                type="button"
                onClick={handleAddTopic}
                className="add-topic-btn"
              >
                Add Topic
              </Button>
            </div>
          </div>
        </div>

        <PriceForm entries={entries} setEntries={setEntries} />

        <CommitteeForm
          committeeMembers={committeeMembers}
          setCommitteeMembers={setCommitteeMembers}
          countriesList={countriesList}
        />

        <Button
          variant="contained"
          sx={{
            backgroundColor: "#c62828",
            marginTop: "20px",
            color: "#fff",
            width: "100%",
            "&:hover": { backgroundColor: "#e63946", color: "#fff" },
          }}
          className="submit-btn"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </div>
    </Drawer>
  );
};

export default ConferencesAdmin;
